/**
 * Copy for the station films (src/components/print-process/Film.jsx): one
 * per service page (PrintProcess.jsx) and all three side by side on the home
 * page (home/HomeServices.jsx), TR / EN / DE. All strings here are NEW
 * (written for the redesign): factual, no claims, no numbers. `aria`
 * summarises the animated stage for screen readers; the station texts are
 * rendered as real text.
 *
 * Pad printing is an indirect process: the image is transferred twice
 * (plate -> pad -> part), so the etching on the cliché is right-reading, the
 * image on the pad is reversed, and the print on the part reads right again.
 * The copy and the drawing follow that.
 *
 * The assembly variant only references functional inspection, which the
 * assembly page already states ("%100 Fonksiyonel kontrol").
 *
 * `stage` (added with the Station Film rebuild) holds the in-stage labels:
 * the Detail A caption, the end-card register label (the services' short
 * names, as in the navbar) and the word on the cycle ruler (the noun of each
 * section title: çevrim / geçiş / adım).
 */
export const PRINT_PROCESS_I18N = {
  pad: {
    tr: {
      title: 'Bir tampon baskı çevrimi',
      lead: 'Baskı, klişeden parçaya iki temasla taşınır: silikon tampon mürekkebi çelik klişeden alır ve parçanın yüzeyine bırakır.',
      aria: 'Tampon baskı çevrimi: mürekkep kabı çelik klişedeki dağlanmış görseli doldurur, tampon klişeye iner ve mürekkebi alır, parçanın üzerine taşır, parçaya bastırır ve kalkar; işaret parçada beyaz olarak basılı kalır.',
      stations: [
        { name: 'Klişe', text: 'Baskı görseli çelik klişeye dağlanır; mürekkep kabı dağlanan alanı mürekkeple doldurur.' },
        { name: 'Tampon', text: 'Silikon tampon mürekkebi klişeden alır; görsel tampon üzerinde ters durur.' },
        { name: 'Parça', text: 'Tampon parçaya bastırılır; esnek olduğu için kavisli ve girintili yüzeyleri izler ve işaret yeniden doğru yönde okunur.' },
      ],
      stage: {
        detail: 'Tampon yüzeyi (alttan)',
        register: { service: 'Tampon Baskı', mark: 'ISOFIX' },
        cycle: 'Çevrim',
      },
    },
    en: {
      title: 'One pad printing cycle',
      lead: 'The print travels from plate to part in two contacts: a silicone pad lifts the ink from a steel cliché and sets it down on the part.',
      aria: 'Pad printing cycle: the ink cup fills the artwork etched into a steel cliché, the pad descends onto the cliché and picks up the ink, carries it over the part, presses onto the part and lifts; the mark stays printed in white on the part.',
      stations: [
        { name: 'Cliché', text: 'The artwork is etched into a steel plate; the ink cup fills the etching.' },
        { name: 'Pad', text: 'The silicone pad picks the ink up from the plate; on the pad the image is reversed.' },
        { name: 'Part', text: 'The pad presses onto the part; being flexible, it follows curved and recessed surfaces, and the mark reads the right way round again.' },
      ],
      stage: {
        detail: 'Pad face (from below)',
        register: { service: 'Pad Printing', mark: 'ISOFIX' },
        cycle: 'Cycle',
      },
    },
    de: {
      title: 'Ein Tampondruck-Zyklus',
      lead: 'Das Druckbild gelangt in zwei Kontakten von der Platte auf das Bauteil: Ein Silikontampon nimmt die Farbe vom Stahlklischee auf und gibt sie auf das Bauteil ab.',
      aria: 'Tampondruck-Zyklus: Der Farbtopf füllt das in ein Stahlklischee geätzte Motiv, der Tampon senkt sich auf das Klischee und nimmt die Farbe auf, trägt sie über das Bauteil, drückt auf das Bauteil und hebt ab; das Zeichen bleibt weiß auf dem Bauteil gedruckt.',
      stations: [
        { name: 'Klischee', text: 'Das Motiv ist in eine Stahlplatte geätzt; der Farbtopf füllt die Ätzung mit Farbe.' },
        { name: 'Tampon', text: 'Der Silikontampon nimmt die Farbe von der Platte auf; auf dem Tampon steht das Bild seitenverkehrt.' },
        { name: 'Bauteil', text: 'Der Tampon wird auf das Bauteil gedrückt; da er flexibel ist, folgt er gewölbten und vertieften Flächen, und das Zeichen ist wieder seitenrichtig lesbar.' },
      ],
      stage: {
        detail: 'Tamponfläche (von unten)',
        register: { service: 'Tampondruck', mark: 'ISOFIX' },
        cycle: 'Zyklus',
      },
    },
  },
  screen: {
    tr: {
      title: 'Bir serigrafi baskı geçişi',
      lead: 'Serigrafide mürekkep, şablonun açık bıraktığı elek gözeneklerinden doğrudan yüzeye geçer; tek bir rakle geçişi baskıyı düz panele aktarır.',
      aria: 'Serigrafi baskı geçişi: mürekkep eleğe dökülür, elek panelin üzerine iner, rakle eleğin üzerinden geçer, elek kalkar ve skala düz panelin üzerinde basılı kalır.',
      stations: [
        { name: 'Elek', text: 'Gergin eleğin üzerindeki şablon yalnızca baskı görselini açık bırakır.' },
        { name: 'Rakle', text: 'Rakle eleğin üzerinden bir kez geçer ve mürekkebi açık gözeneklerden iter.' },
        { name: 'Panel', text: 'Elek kalktığında skala ve işaretler düz panelin üzerinde kalır.' },
      ],
      stage: {
        register: { service: 'Serigrafi Baskı', mark: 'Skala' },
        cycle: 'Geçiş',
      },
    },
    en: {
      title: 'One screen printing pass',
      lead: 'In screen printing the ink passes straight through the open mesh of the stencil; one squeegee pass lays the print onto a flat panel.',
      aria: 'Screen printing pass: ink is flooded onto the screen, the screen lowers over the panel, the squeegee sweeps across it, the screen lifts and the scale stays printed on the flat panel.',
      stations: [
        { name: 'Screen', text: 'A stencil on the tensioned mesh leaves only the artwork open.' },
        { name: 'Squeegee', text: 'The squeegee crosses the screen once and pushes the ink through the open mesh.' },
        { name: 'Panel', text: 'When the screen lifts, the scale and legends stay on the flat panel.' },
      ],
      stage: {
        register: { service: 'Screen Printing', mark: 'Scale' },
        cycle: 'Pass',
      },
    },
    de: {
      title: 'Ein Siebdruck-Durchgang',
      lead: 'Beim Siebdruck geht die Farbe direkt durch die offenen Maschen der Schablone; ein Rakelzug überträgt das Druckbild auf ein flaches Bauteil.',
      aria: 'Siebdruck-Durchgang: Farbe wird auf das Sieb geflutet, das Sieb senkt sich über das Bauteil, die Rakel fährt darüber, das Sieb hebt ab und die Skala bleibt auf dem flachen Bauteil gedruckt.',
      stations: [
        { name: 'Sieb', text: 'Eine Schablone auf dem gespannten Gewebe lässt nur das Motiv offen.' },
        { name: 'Rakel', text: 'Die Rakel fährt einmal über das Sieb und drückt die Farbe durch die offenen Maschen.' },
        { name: 'Bauteil', text: 'Hebt sich das Sieb, bleiben Skala und Beschriftung auf dem flachen Bauteil.' },
      ],
      stage: {
        register: { service: 'Siebdruck', mark: 'Skala' },
        cycle: 'Durchgang',
      },
    },
  },
  assembly: {
    tr: {
      title: 'Bir montaj adımı',
      lead: 'Plastik bileşenler tek bir parçada birleştirilir ve fonksiyonel kontrolden geçer.',
      aria: 'Montaj adımı: basılı kapak gövdenin üzerine hizalanır, gövdeye kaydırılıp yerine oturur ve birleşen parça fonksiyonel kontrolden geçer.',
      stations: [
        { name: 'Bileşenler', text: 'Basılı kapak ve gövde ayrı parçalar olarak hazırlanır ve aynı eksende hizalanır.' },
        { name: 'Montaj', text: 'Kapak gövdeye kaydırılır ve yerine oturur.' },
        { name: 'Kontrol', text: 'Birleşen parça fonksiyonel kontrolden geçer.' },
      ],
      stage: {
        register: { service: 'Montaj & Trim', mark: 'ISOFIX' },
        cycle: 'Adım',
      },
    },
    en: {
      title: 'One assembly step',
      lead: 'Plastic components are joined into one part and pass a functional inspection.',
      aria: 'Assembly step: the printed cover is aligned over its housing, slides in and clicks into place, and the assembled part passes a functional inspection.',
      stations: [
        { name: 'Components', text: 'The printed cover and its housing are prepared as separate parts and aligned on one axis.' },
        { name: 'Assembly', text: 'The cover slides into the housing and clicks into place.' },
        { name: 'Inspection', text: 'The assembled part passes a functional inspection.' },
      ],
      stage: {
        register: { service: 'Assembly & Trim', mark: 'ISOFIX' },
        cycle: 'Step',
      },
    },
    de: {
      title: 'Ein Montageschritt',
      lead: 'Kunststoffkomponenten werden zu einem Bauteil gefügt und funktional geprüft.',
      aria: 'Montageschritt: Die bedruckte Abdeckung wird über dem Gehäuse ausgerichtet, eingeschoben und rastet ein; das gefügte Bauteil durchläuft eine Funktionsprüfung.',
      stations: [
        { name: 'Komponenten', text: 'Bedruckte Abdeckung und Gehäuse liegen als Einzelteile bereit und werden auf einer Achse ausgerichtet.' },
        { name: 'Montage', text: 'Die Abdeckung wird in das Gehäuse geschoben und rastet ein.' },
        { name: 'Prüfung', text: 'Das gefügte Bauteil durchläuft eine Funktionsprüfung.' },
      ],
      stage: {
        register: { service: 'Montage & Trim', mark: 'ISOFIX' },
        cycle: 'Schritt',
      },
    },
  },
}

/** The pause / play button on every film (WCAG 2.2.2). */
export const PRINT_PROCESS_CONTROLS = {
  tr: { pause: 'Animasyonu duraklat', play: 'Animasyonu oynat' },
  en: { pause: 'Pause animation', play: 'Play animation' },
  de: { pause: 'Animation anhalten', play: 'Animation abspielen' },
}
